import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getStudentsOfflineNew, getTeachersOffline, addCompensatoryOrRestrictedClass, sendSlackAlert, getStudentsSlotDetails } from "../../../../Api/apiUtils";
import { ConfigContext } from "../../../../Api/global/ApiContext";
import {
  Button,
  Callout,
  Card,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  TextArea,
} from "../../../../ui";
import "./addClass.scss";

interface Student {
  studentId: string;
  credentialId: string;
  userId: any;
  id:any
  studentName: string;
  name:any;
  emailId: string;
  mobileNumber: string;
  profilePhoto?: string;
  countryCode: any;
  classesLeft?: number;
  compensatoryClassesLeft?: number;
}

interface Teacher {
  teacherId?: string | number;
  id?: string | number;
  userId?:any;
  name?: string;
  teacherName?: string;
  workingStatus?: string;
}

interface OfflineCentre {
  centerId: number;
  centreId: number;
  centreName: string;
  name: string;
  city: string;
}

interface Course {
  courseId: number;
  courseName: string;
  batchOfflineCourse?: boolean;
  isDemoCourse?: boolean;
}

type ClassType = "compensatory" | "restricted" | null;

const AddClass: React.FC = () => {
  const config = useContext(ConfigContext);
  const prevCourseIdRef = useRef<number | null>(null);
  
  //  1: Class Type Selection
  const [classType, setClassType] = useState<ClassType>(null);
  
  // Center Selection
  const [selectedCenter, setSelectedCenter] = useState<OfflineCentre | null>(null);
  const [centers, setCenters] = useState<OfflineCentre[]>([]);

  //  2: Student Search
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);

  //  3: Selected Student
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  //  4: Teacher Selection
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [loadingTeachers, setLoadingTeachers] = useState<boolean>(false);

  //  5: Day/Time Selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [selectedVirtualSlotId, setSelectedVirtualSlotId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<any | null>(null);
  
  // Course Selection
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  //  6: Reasons
  const [reason, setReason] = useState<string>("");

  //  7: Renewal Date (Restricted only)
  const [renewalDate, setRenewalDate] = useState<Date | null>(null);

  //  8: Confirmation
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  //  9: Success
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Generate time options (30-minute intervals)
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        times.push(timeString);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Debounce function for search
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // Fetch students
  const fetchStudents = async (searchPayload: { offlineCenterId?:any; courseId?: any; studentName?: string;} = {}) => {
    // Use centerId from payload or selectedCenter
    const centerId = searchPayload.offlineCenterId || selectedCenter?.centerId;
    // Use courseId from payload or selectedCourse
    const courseId = searchPayload.courseId || selectedCourse?.courseId;
    
    if (!centerId) {
      setStudents([]);
      setFilteredStudents([]);
      return;
    }
    
    setLoadingStudents(true);
    try {
      const searchTerm = searchPayload.studentName || searchQuery || "";
      const response = await getStudentsOfflineNew(courseId, centerId, searchTerm);
      const fetchedStudents = response.content || [];


      if (Array.isArray(fetchedStudents)) {
        setStudents(fetchedStudents);
        filterStudents(fetchedStudents);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to fetch students");
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Filter students based on class type
  const filterStudents = (studentList: Student[]) => {
    if (classType === "restricted") {
      // For restricted: only students with 0 classes left and 0 compensatory classes left
      const filtered = studentList.filter(
        (student) =>
          (student.classesLeft === 0 || student.classesLeft === undefined) &&
          (student.compensatoryClassesLeft === 0 || student.compensatoryClassesLeft === undefined)
      );
      setFilteredStudents(filtered);
    } else {
      // For compensatory: all students
      setFilteredStudents(studentList);
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!selectedCenter?.centerId) {
        return;
      }

      const payload: { offlineCenterId: any; courseId?: any; studentName?: string } = {
        offlineCenterId: selectedCenter.centerId,
        courseId: selectedCourse?.courseId
      };

      if (!query.trim()) {
        await fetchStudents(payload);
        return;
      }

      payload.studentName = query;
      await fetchStudents(payload);
    }, 500),
    [classType, selectedCenter, selectedCourse]
  );

  // Fetch teachers
  const fetchTeachers = async () => {
    if (!selectedCenter) {
      setTeachers([]);
      return;
    }
    
    setLoadingTeachers(true);
    try {
      // Call getTeachersOffline with centerId and optional courseId (not passing courseId)
      const response = await getTeachersOffline(selectedCenter.centerId);
      
      let teachersData: any[] = [];
      
      if (response?.content && Array.isArray(response.content)) {
        teachersData = response.content;
      } else if (Array.isArray(response)) {
        teachersData = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          teachersData = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          teachersData = response.data.content;
        }
      }
      
      // Normalize teacher data: map 'id' to 'teacherId' if needed
      const normalizedTeachers = teachersData.map((teacher: any) => ({
        ...teacher,
        teacherId: teacher.teacherId || teacher.id,
        name: teacher.name || teacher.teacherName,
      }));
      
      setTeachers(normalizedTeachers);
    } catch (error) {
      console.error("Error fetching teachers:", error);
      toast.error("Failed to fetch teachers");
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Handle class type selection
  const handleClassTypeSelect = (type: ClassType) => {
    setClassType(type);
    // Reset all form data
    setSelectedCenter(null);
    setSearchQuery("");
    setSelectedStudent(null);
    setSelectedTeacher(null);
    setSelectedDate(null);
    setSelectedTime("");
    setReason("");
    setRenewalDate(null);
    setStudents([]);
    setFilteredStudents([]);
    setSelectedCourse(null);
  };

  // Load centers from config
  useEffect(() => {
    if (config?.offlineCentres && Array.isArray(config.offlineCentres)) {
      setCenters(config.offlineCentres);
    }
  }, [config?.offlineCentres]);

  // Get courses from config (same as offlineBatch.tsx)
  const getCourseOptions = () => {
    if (!config?.grmpcourses || !Array.isArray(config.grmpcourses)) {
      return [];
    }
    
    // Filter for offline courses (courses with "offline" in the name)
    // Use v2Id as the value to pass to the API
    const offlineCourses = config.grmpcourses.filter(course => 
      course.name && course.name.toLowerCase().includes('offline')
    );
    
    return offlineCourses.map((course: any) => ({
      courseId: course.v2Id || course.id || course.courseId,
      courseName: course.name || course.courseName,
      batchOfflineCourse: course.batchOfflineCourse,
      isDemoCourse: course.isDemoCourse
    }));
  };

  // Handle student selection
  const handleStudentSelect = (student: Student) => {
    if (classType === "restricted") {
      // Validate for restricted class
      if (
        student.classesLeft !== 0 &&
        student.classesLeft !== undefined &&
        student.compensatoryClassesLeft !== 0 &&
        student.compensatoryClassesLeft !== undefined
      ) {
        toast.error("Only students with 0 classes left and 0 compensatory classes left can be selected for restricted classes");
        return;
      }
    }
    setSelectedStudent(student);
  };

  // Validate form before submission
  const validateForm = (): boolean => {
    if (!classType) {
      toast.error("Please select a class type");
      return false;
    }
    if (!selectedCenter) {
      toast.error("Please select a center");
      return false;
    }
    if (!selectedStudent) {
      toast.error("Please select a student");
      return false;
    }
   
    if (!selectedDate) {
      toast.error("Please select a date");
      return false;
    }
    if (!selectedTime) {
      toast.error("Please select a time");
      return false;
    }
    if (!selectedSlotId && !selectedVirtualSlotId) {
      toast.error("Please select a valid time slot");
      return false;
    }
    if (!selectedCourse) {
      toast.error("Please select a course");
      return false;
    }
    if (!reason.trim()) {
      toast.error("Please enter a reason");
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setShowConfirmModal(true);
  };

  // Confirm and submit
  const handleConfirmSubmit = async () => {
    if (!selectedSlotId && !selectedVirtualSlotId) {
      toast.error('Please select a valid time slot');
      return;
    }
    
    if (!selectedCourse) {
      toast.error('Course is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare payload for new API - use slotId if available, otherwise use virtualSlotId
      const payload: any = {
        studentId: parseInt(selectedStudent!.id),
        courseId: selectedCourse!.courseId,
        classType: classType === "compensatory" ? "COMPENSATORY" : "RESTRICTED",
        additionalNotes: reason,
      };

      // Add slotId or virtualSlotId based on availability
      // if (selectedSlotId) {
      //   payload.slotId = selectedSlotId;
      // } else if (selectedVirtualSlotId) {
      //   payload.virtualSlotId = selectedVirtualSlotId;
      // }

      // Add compensatory/restricted class
      const response = await addCompensatoryOrRestrictedClass(  selectedSlotId ? selectedSlotId : selectedVirtualSlotId , payload);

      // Trigger Slack alert
    //   await triggerSlackAlert(payload);

      // Show success message
      setSuccessMessage(
        `Successfully added ${classType === "compensatory" ? "Compensatory" : "Restricted"} class for ${selectedStudent?.name}`
      );
      setShowConfirmModal(false);
      setShowSuccessModal(true);

      // Reset form
      resetForm();
    } catch (error: any) {
      console.error("Error adding class:", error);
      toast.error(error?.response?.data?.message || "Failed to add class");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Trigger Slack alert
//   const triggerSlackAlert = async (payload: any) => {
//     try {
//       const alertData = {
//         alertType: classType === "compensatory" ? "Compensation Class" : "Restricted Class Added",
//         studentName: selectedStudent!.studentName,
//         teacherName: selectedTeacher!.teacherName,
//         addedDate: new Date().toISOString().split("T")[0],
//         scheduledDateTime: payload.scheduledDateTime,
//         reason: payload.reason,
//         expectedRenewal: payload.renewalDate || "-",
//       };

//       // Send Slack alert
//       await sendSlackAlert(alertData);
//     } catch (error) {
//       console.error("Error triggering Slack alert:", error);
//       // Don't fail the main operation if Slack alert fails
//     }
//   };

  // Reset form
  const resetForm = () => {
    setSelectedCenter(null);
    setSearchQuery("");
    setSelectedStudent(null);
    setSelectedTeacher(null);
    setSelectedDate(null);
    setSelectedTime("");
    setReason("");
    setRenewalDate(null);
    setStudents([]);
    setFilteredStudents([]);
    setAvailableSlots([]);
    setSelectedSlotId(null);
    setSelectedVirtualSlotId(null);
    setSelectedSlot(null);
  };

  // Fetch teachers when center is selected
  useEffect(() => {
    if (selectedCenter && classType) {
      fetchTeachers();
    } else {
      setTeachers([]);
    }
  }, [selectedCenter, classType]);

  // Update courses when center is selected (same as offlineBatch.tsx)
  useEffect(() => {
    if (selectedCenter && classType) {
      const courseOptions = getCourseOptions();
      setFilteredCourses(courseOptions);
      // Reset course selection when center changes
      setSelectedCourse(null);
    } else {
      setFilteredCourses([]);
      setSelectedCourse(null);
    }
  }, [selectedCenter, classType, config?.grmpcourses]);

  // Reset student selection when course changes
  useEffect(() => {
    const currentCourseId = selectedCourse?.courseId || null;
    
    // Only reset if course actually changed (not on initial selection)
    if (prevCourseIdRef.current !== null && prevCourseIdRef.current !== currentCourseId && selectedStudent) {
      // Reset student selection and related fields when course changes
      setSelectedStudent(null);
      setSearchQuery("");
      setAvailableSlots([]);
      setSelectedTime("");
      setSelectedSlotId(null);
      setSelectedVirtualSlotId(null);
      setSelectedSlot(null);
    }
    
    // Update ref with current course ID
    prevCourseIdRef.current = currentCourseId;
  }, [selectedCourse?.courseId, selectedStudent]);

  // Fetch students when center and course are selected
  useEffect(() => {
    if (selectedCenter && selectedCourse && classType) {
      fetchStudents({ offlineCenterId: selectedCenter.centerId, courseId: selectedCourse.courseId });
    } else {
      setStudents([]);
      setFilteredStudents([]);
    }
  }, [selectedCenter, selectedCourse, classType]);

  // Fetch available slots when student and date are selected
  useEffect(() => {
    if (selectedStudent?.id && selectedDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
      setSelectedTime("");
      setSelectedSlot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, selectedDate]);

  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAvailableSlots = async () => {
    if (!selectedStudent?.id || !selectedDate) return;
    
    setLoadingSlots(true);
    try {
      // Use local timezone instead of UTC to avoid date shifting
      const fromDate = formatDateLocal(selectedDate);
      const toDate = formatDateLocal(selectedDate);
      
      const response = await getStudentsSlotDetails(selectedStudent.id, fromDate, toDate);
      const slots = response?.slots || [];
      
      // Filter slots for the selected date
      const slotsForDate = slots.filter((slot: any) => slot.slotDate === fromDate);
      
      // Sort by start time
      slotsForDate.sort((a: any, b: any) => {
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      
      // Store full slot data (not just times) so we can get slotId when time is selected
      setAvailableSlots(slotsForDate);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      toast.error('Failed to fetch available slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Filter students when class type changes
  useEffect(() => {
    if (students.length > 0) {
      filterStudents(students);
    }
  }, [classType, students]);

  const classTypeLabel = classType === "compensatory" ? "Compensatory" : "Restricted";

  return (
    <Page>
      <PageHeader
        eyebrow="Offline"
        title="Add Class"
        subtitle="Add a compensatory or restricted class for an offline student."
      />

      <Card title="New class" className="add-class-card">
        <Field label="Type of class to add" required>
          {() => (
            <SegmentedControl<"compensatory" | "restricted">
              label="Type of class to add"
              value={(classType ?? "") as "compensatory" | "restricted"}
              onChange={(type) => {
                if (type !== classType) handleClassTypeSelect(type);
              }}
              options={[
                { value: "compensatory", label: "Compensatory" },
                { value: "restricted", label: "Restricted" },
              ]}
            />
          )}
        </Field>

        {classType && (
          <>
            <div className="add-class__grid">
              <Field label="Center" required>
                {(ctl) => (
                  <Select
                    {...ctl}
                    value={selectedCenter?.centerId ? String(selectedCenter.centerId) : ""}
                    onChange={(e) => {
                      const center = centers.find((c) => c.centerId === parseInt(e.target.value));
                      setSelectedCenter(center || null);
                      setSelectedTeacher(null); // Reset teacher when center changes
                    }}
                    options={[
                      { value: "", label: "Select a center" },
                      ...centers.map((center) => ({
                        value: String(center.centerId),
                        label: `${center.centreName || center.name} ${center.city ? `- ${center.city}` : ""}`,
                      })),
                    ]}
                  />
                )}
              </Field>

              {selectedCenter && (
                <Field
                  label="Course"
                  required
                  error={filteredCourses.length === 0 ? "No offline batch courses found for this center" : undefined}
                >
                  {(ctl) => (
                    <Select
                      {...ctl}
                      value={selectedCourse ? String(selectedCourse.courseId) : ""}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === "") {
                          setSelectedCourse(null);
                          setStudents([]);
                          setFilteredStudents([]);
                        } else {
                          // Find course by matching courseId
                          const course = filteredCourses.find((c) => String(c.courseId) === selectedValue);
                          if (course) {
                            setSelectedCourse(course);
                          } else {
                            console.error("Course not found for ID:", selectedValue);
                            setSelectedCourse(null);
                          }
                        }
                      }}
                      options={[
                        {
                          value: "",
                          label:
                            filteredCourses.length === 0
                              ? "No offline batch courses available for this center"
                              : "Select a course",
                        },
                        ...filteredCourses.map((course) => ({
                          value: String(course.courseId),
                          label: course.courseName,
                        })),
                      ]}
                    />
                  )}
                </Field>
              )}
            </div>

            {selectedCenter && selectedCourse && !selectedStudent && (
              <div className="add-class">
                <Field label="Search for student" hint="Pick a student from the list below.">
                  {(ctl) => (
                    <SearchInput
                      {...ctl}
                      placeholder="Search by name, email, or mobile number..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      disabled={loadingStudents}
                    />
                  )}
                </Field>

                {classType === "restricted" && (
                  <Callout tone="info">
                    Only students with 0 classes left and 0 compensatory classes left can be selected.
                  </Callout>
                )}

                {loadingStudents ? (
                  <Loader label="Loading students…" />
                ) : filteredStudents.length > 0 ? (
                  <ul className="add-class__students" aria-label="Students">
                    {filteredStudents.map((student) => (
                      <li key={student.id}>
                        <button
                          type="button"
                          className="add-class__student"
                          onClick={() => handleStudentSelect(student)}
                        >
                          {student.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState
                    title="No students found"
                    description={
                      classType === "restricted"
                        ? "No student at this center and course has 0 classes left. Try another name, course or center."
                        : "No student at this center and course matches. Try another name, course or center."
                    }
                  />
                )}
              </div>
            )}

            {selectedStudent && selectedCenter && selectedCourse && (
              <>
                <Callout tone="success" title="Selected student">
                  <div className="add-class__selected">
                    <span className="ds-strong">{selectedStudent.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedStudent(null);
                        setSearchQuery("");
                      }}
                    >
                      Change student
                    </Button>
                  </div>
                </Callout>

                <div className="add-class__grid">
                  <Field label="Date" required className="add-class__date">
                    {(ctl) => (
                      <DatePicker
                        id={ctl.id}
                        selected={selectedDate}
                        onChange={(date: Date | null) => setSelectedDate(date)}
                        // minDate={new Date()}
                        dateFormat="yyyy-MM-dd"
                        className="ds-input"
                        placeholderText="Select date"
                      />
                    )}
                  </Field>
                  <Field
                    label="Available slot"
                    required
                    hint={loadingSlots ? "Loading available slots…" : undefined}
                  >
                    {(ctl) => (
                      <select
                        {...ctl}
                        className="ds-select"
                        value={selectedTime}
                        onChange={(e) => {
                          setSelectedTime(e.target.value);
                          // Find the slot for the selected time
                          const slot = availableSlots.find((slot: any) => {
                            const timeFormatted = slot.startTime ? slot.startTime.substring(0, 5) : "";
                            return timeFormatted === e.target.value;
                          });
                          setSelectedSlot(slot || null);
                          setSelectedSlotId(slot?.studentSlotId);
                          setSelectedVirtualSlotId(slot?.virtualSlotId || null);
                        }}
                        disabled={!selectedStudent?.id || !selectedDate || loadingSlots}
                      >
                        <option value="">
                          {loadingSlots
                            ? "Loading slots..."
                            : !selectedStudent?.id || !selectedDate
                              ? "Select student and date first"
                              : "Select time"}
                        </option>
                        {availableSlots.length > 0 ? (
                          availableSlots.map((slot: any, index: number) => {
                            // Format time from "HH:MM:SS" to "HH:MM"
                            const timeFormatted = slot.startTime ? slot.startTime.substring(0, 5) : "";
                            return (
                              <option key={slot.studentSlotId || slot.teacherSlotId || index} value={timeFormatted}>
                                {timeFormatted}
                              </option>
                            );
                          })
                        ) : selectedStudent?.id && selectedDate && !loadingSlots ? (
                          <option value="" disabled>
                            No available slots
                          </option>
                        ) : null}
                      </select>
                    )}
                  </Field>
                </div>

                <Field label="Reason" required>
                  {(ctl) => (
                    <TextArea
                      {...ctl}
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Enter reason for adding this class..."
                    />
                  )}
                </Field>

                <div className="add-class__actions">
                  <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Add Class"}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Card>

      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={`Add ${classTypeLabel.toLowerCase()} class?`}
        actions={
          <>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmSubmit} loading={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Confirm"}
            </Button>
          </>
        }
      >
        <p>Please review the details before confirming.</p>
        <DescriptionList
          items={[
            { label: "Student", value: selectedStudent?.name },
            { label: "Teacher", value: selectedSlot?.teacher?.name || "N/A" },
            { label: "Date & time", value: `${selectedDate?.toLocaleDateString() ?? ""} at ${selectedTime}` },
            { label: "Reason", value: reason },
            ...(classType === "restricted" && renewalDate
              ? [{ label: "Expected renewal", value: renewalDate.toLocaleDateString() }]
              : []),
          ]}
        />
      </Dialog>

      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Class added"
        actions={
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>
            Close
          </Button>
        }
      >
        <Callout tone="success">{successMessage}</Callout>
      </Dialog>
    </Page>
  );
};

export default AddClass;
